import React, { useContext, useMemo } from 'react';

import { Box, LinearProgress } from '@material-ui/core';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

import {
  ActivateSubscriptionDialog,
  CommonNavBarBlock,
  SuccessSubscriptionDialog,
} from '@vyce/core/src/components';
import { DeviceContext, TeamsPageProvider } from '@vyce/core/src/contexts';
import { getPaddingForContent } from '@vyce/core/src/utils';
import { getIdFromUrlSegment } from '@vyce/core/src/utils/url';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { InvitePreselectedTarget } from '@vyce/core/src/views/invites/types';

import {
  GetStartedModule,
  PendingInvitesModule,
  TeamMembersModule,
  TeamModule,
  TeamsDashboardModule,
  TeamsListModule,
  UniversalInviteModule,
} from 'src/modules';
import { useTypedSelector } from 'src/hooks';

import { useStyles } from './styles';
import { TEAMS_TABS } from './config';
import { useTeamsData } from './hooks';
import { TeamsSettingsPage } from './TeamsSettingsPage';
import { TeamsReports } from './TeamsReports';

export const EmployerTeams: React.FC = () => {
  const { isMobile } = useContext(DeviceContext);
  const { selectedCompanyAppData, selectedCompany } = useTypedSelector(state => state.helper);
  const { companyDataLoading, teams } = selectedCompanyAppData || {};
  const padding = getPaddingForContent(isMobile);

  const { filterTabs } = usePermissions();

  const tabs = useMemo(() => filterTabs(TEAMS_TABS), [filterTabs]);

  const {
    loading,
    contextValue,
    isOpenSuccessDialog,
    firstIdOfTeam,
    isInviteFormOpen,
    selectedCompanyIsSubscribed,
    hasPermissions,
    hasBillingPermissions,
    subscribeLoading,
    isActivateSubscriptionDialogOpen,
    hadSubscription,
    simpleSubscribe,
    setOpenSuccessDialog,
    setCloseInviteForm,
    closeActivateSubscriptionDialog,
  } = useTeamsData();
  const classes = useStyles();

  // One dialog serves every /teams tab, so the team comes from the route: an invite sent from
  // a team page — or straight after signing up, through ?invite=true — is meant for that team.
  const teamRouteMatch = useRouteMatch<{ team: string }>({ path: '/teams/list/:team' });
  const teamId = getIdFromUrlSegment(teamRouteMatch?.params.team);
  const inviteTarget = useMemo<InvitePreselectedTarget | undefined>(
    () => (teamId ? { module: 'teams', resourceId: teamId } : undefined),
    [teamId]
  );

  const teamsText = useMemo(() => {
    return selectedCompanyAppData?.teams?.length ? 'Add team members now' : 'Create a Team';
  }, [selectedCompanyAppData?.teams?.length]);

  const payrollText = useMemo(() => {
    return selectedCompanyAppData?.paySchedules?.length ? 'Add to Payroll' : 'Set Up Payroll';
  }, [selectedCompanyAppData?.paySchedules]);

  const timeText = useMemo(() => {
    return selectedCompanyAppData?.locations?.length
      ? 'Add to Time & Attendance'
      : 'Set up Time & Attendance';
  }, [selectedCompanyAppData?.locations]);

  if (companyDataLoading || (loading && !teams?.length)) {
    return <LinearProgress />;
  }

  if (!hasPermissions) {
    return <GetStartedModule hasPermissions={hasPermissions} />;
  }

  if ((!selectedCompanyIsSubscribed && hasBillingPermissions) || (!teams?.length && !loading)) {
    return (
      <TeamsPageProvider value={contextValue}>
        <GetStartedModule hadSubscription={hadSubscription} />
      </TeamsPageProvider>
    );
  }

  return (
    <TeamsPageProvider value={contextValue}>
      <Box height="100%">
        <Box className={classes.moduleContainer}>
          <CommonNavBarBlock tabItems={tabs} />

          <Box padding={padding} display="flex" flexDirection="column" className={classes.container}>
            <Switch>
              <Route path="/teams/dashboard" component={TeamsDashboardModule} />
              <Route path="/teams/members" component={TeamMembersModule} />
              <Route path="/teams/pending-invites" component={PendingInvitesModule} />
              <Route exact path="/teams/list" component={TeamsListModule} />
              <Route exact path="/teams/list/:team" component={TeamModule} />
              <Route path="/teams/settings" component={TeamsSettingsPage} />
              <Route path="/teams/reports" component={TeamsReports} />
              <Route path="/teams" render={() => <Redirect to={{ pathname: '/teams/dashboard' }} />} />
            </Switch>
          </Box>
        </Box>
      </Box>
      <SuccessSubscriptionDialog
        open={isOpenSuccessDialog}
        setOpen={setOpenSuccessDialog}
        teamsText={teamsText}
        payrollText={payrollText}
        timeText={timeText}
        withPayroll
        withTeams
        withTime
        withForms
        teamsLink={firstIdOfTeam && `/teams/list/${firstIdOfTeam}?invite=true`}
      />
      <UniversalInviteModule
        onClose={setCloseInviteForm}
        isOpen={isInviteFormOpen}
        preselectedTarget={inviteTarget}
      />
      <ActivateSubscriptionDialog
        open={isActivateSubscriptionDialogOpen}
        onClose={closeActivateSubscriptionDialog}
        callback={simpleSubscribe}
        loading={subscribeLoading}
        wasSubscribed={selectedCompany?.subscribed_company}
      />
    </TeamsPageProvider>
  );
};
